/**
 * DanosColaboradorView -- danos y entradas de ajuste, por colaborador y por periodo.
 *
 * MISION CIERRE-CONTEO-V1-001, tarea CC-COLAB-T3.
 *
 * AUDIENCIA: el DUENO o el administrativo, en el panel, en escritorio. Ve a TODOS sus
 * colaboradores. La otra mitad de esta tarea -que el colaborador vea SOLO lo suyo, en
 * primera persona- vive en la ficha del arcade y NO se mezcla con esta.
 *
 * POR QUE ESTA VISTA SEPARA EN TRES CAPAS
 * ---------------------------------------
 * El campo que guarda "quien" en un movimiento NO dice quien lo hizo: dice quien tenia
 * la sesion abierta en el Desktop (hallazgo de Ares, #9589). Duvan es el cajero, asi
 * que los ajustes heredaban su usuario. Medido: por usuario carga 248 ajustes de
 * agosto; cruzado contra las sesiones de conteo son 85 suyos, 76 de Brayan y 48 de
 * Camilo. 124 ajustes que figuraban como de Duvan eran de otros.
 *
 * Por eso el endpoint devuelve cada fila con su CAPA y esta vista las pinta separadas:
 *
 *   conteo      -> el ajuste nacio de una sesion y se atribuye a QUIEN CONTO.
 *                  Es la unica capa con la que se puede comparar personas.
 *   usuario     -> no hubo sesion: correccion manual, atribuida al usuario del Desktop.
 *                  El nombre es el de quien tenia la sesion, no necesariamente quien lo hizo.
 *   propietario -> el dueno.
 *   sin identificar -> historico viejo, sin usuario. Aparte y fuera de toda comparacion.
 *
 * Mezclar las cuatro en una sola tabla ordenada por total seria un podio construido
 * sobre quien deja la sesion abierta. Eso no es un ranking: es una injusticia con cara
 * de dato.
 *
 * DOCTRINA DE LOS DOS MUNDOS: solo Ajuste-Dano y Ajuste-Entrada son anomalos y
 * auditables. Desperdicio y Aumento son operacion normal y NO se le cargan a nadie.
 *
 * FAIL-OPEN: si el endpoint no esta publicado, la vista lo DICE con su causa en vez de
 * mostrar ceros. Un cero sin causa el dueno lo lee como "no paso nada".
 *
 * @module presentation/views/DanosColaboradorView
 */

import { MovimientosColaboradorApiClient } from '../../infrastructure/api/MovimientosColaboradorApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { setPageTitle } from '../components/NavBar.js';
import { textoDeFiabilidad, advertenciaAMostrar } from '../../application/indicadores/textosDeDanos.js';
import { DanosVigentesApiClient } from '../../infrastructure/api/DanosVigentesApiClient.js';
import { normalizarDanosVigentes, filaDeDano, cuantosSinCosto, danosVisibles, aplicadoPorSolicitud } from '../../application/indicadores/danosVigentes.js';

const pesos = (n) => '$ ' + Math.round(Number(n) || 0).toLocaleString('es-CO');

/**
 * INV-CRUCES-PRESENTACION-T1 (D-138, ampliacion 3 de Ares #31237): los danos del mes uno por uno, solo lo vigente. Un
 * dano compensado del todo no se lista y uno compensado en parte muestra lo que queda; ni tachado, ni etiqueta, ni bruto.
 * El pie da el total del servidor (= indicador). Aqui no se suma nada. Sin la ruta, lo dice y no pinta ceros.
 */
async function pintarVigentes(bizId, r) {
  const $el = document.getElementById('nx-dc-vigentes');
  if (!$el) return;
  $el.innerHTML = `<div style="color:${C.suave};font-size:14px">Cargando los danos del mes...</div>`;
  const res = await DanosVigentesApiClient.get(bizId, { desde: r.desde, hasta: r.hasta });
  if (!document.getElementById('nx-dc-vigentes')) return;
  if (!res.ok) {
    const porQue = res.motivo === 'NO_PUBLICADO' ? 'La lista de danos con sus compensaciones todavia no esta publicada en la API.'
      : res.motivo === 'PROHIBIDO' ? 'Esta lista es solo para el dueno o el administrativo.'
      : res.motivo === 'SIN_SESION' ? 'Tu sesion vencio: vuelve a entrar.'
      : 'No pudimos traer la lista de danos ahora.';
    $el.innerHTML = `<div data-dc-vigentes-falta style="color:${C.suave};font-size:14px;border:1px dashed ${C.borde};border-radius:8px;padding:10px 12px">${esc(porQue)}</div>`;
    return;
  }
  const d = normalizarDanosVigentes(res.data);
  const visibles = danosVisibles(d.items);
  const porSolicitud = bloquePorSolicitud(aplicadoPorSolicitud(d.items));
  if (!visibles.length) {
    $el.innerHTML = `<div style="color:${C.suave};font-size:14px">Sin danos en ${esc(r.etiqueta)}.</div>${porSolicitud}`;
    return;
  }
  const sinCosto = cuantosSinCosto(d.items);
  const filas = visibles.map((it) => {
    const f = filaDeDano(it);
    const valor = f.sinCosto ? `<span data-dc-sin-costo style="color:${C.alerta}">sin costo</span>` : `<span data-dc-valor>${pesos(f.valor)}</span>`;
    return `<tr style="border-top:1px solid ${C.borde}">
      <td style="padding:8px 6px;white-space:nowrap">${esc(it.fecha)}</td>
      <td style="padding:8px 6px">${esc(it.insumo)}</td>
      <td style="padding:8px 6px;text-align:right;white-space:nowrap">${valor}</td>
    </tr>`;
  }).join('');
  $el.innerHTML = `
    <h3 style="margin:0 0 4px;font-size:15px">Danos del mes, uno por uno</h3>
    <p style="margin:0 0 8px;color:${C.suave};font-size:14px">Cada dano cuenta en la fecha en que se registro.</p>
    <table style="width:100%;border-collapse:collapse;font-size:14px;color:${C.texto}">
      <thead><tr style="color:${C.suave};text-align:left"><th style="padding:6px">Fecha</th><th style="padding:6px">Insumo</th><th style="padding:6px;text-align:right">Valor</th></tr></thead>
      <tbody>${filas}</tbody>
    </table>
    <div data-dc-vigentes-pie style="display:flex;justify-content:flex-end;flex-wrap:wrap;gap:16px;margin-top:8px;font-size:14px">
      ${sinCosto ? `<span data-dc-sin-costo-pie style="color:${C.alerta}">${sinCosto} ${sinCosto === 1 ? 'dano sin costo (no suma al total)' : 'danos sin costo (no suman al total)'}</span>` : ''}
      ${d.totalVigente !== null ? `<strong data-dc-total>Total del mes ${pesos(d.totalVigente)}</strong>` : ''}
    </div>${porSolicitud}`;
}

const unidades = (n, u) => `${Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 3 })}${u ? ' ' + u : ''}`;

/**
 * PANEL-CRUCES-POR-SOLICITUD-T1: lo aplicado por cada solicitud aprobada (T###), con quien autorizo, las unidades y lo
 * que queda de cada dano. Sin solicitudes (o sin el campo en la API) no pinta nada: se enciende sola.
 */
function bloquePorSolicitud(grupos) {
  if (!grupos.length) return '';
  const tarjetas = grupos.map((g) => {
    const filas = g.filas.map((f) => `<li data-dc-sol-fila style="padding:6px 0;border-top:1px solid ${C.borde};display:flex;flex-wrap:wrap;gap:4px 12px">
        <span style="white-space:nowrap">${esc(f.fecha)}</span>
        <span style="flex:1 1 140px;min-width:0;overflow-wrap:anywhere">${esc(f.insumo)}</span>
        <span style="white-space:nowrap">se ajustaron ${esc(unidades(f.unidades, f.unidad))}</span>
        <span data-dc-sol-queda style="white-space:nowrap;color:${f.queda > 0 ? C.alerta : C.suave}">${f.queda > 0 ? 'quedan ' + esc(unidades(f.queda, f.unidad)) : 'no queda nada'}</span>
      </li>`).join('');
    return `<div data-dc-solicitud="${esc(g.solicitud)}" style="border:1px solid ${C.borde};border-radius:8px;padding:10px 12px;margin-top:8px">
      <div style="display:flex;flex-wrap:wrap;gap:4px 12px;align-items:baseline">
        <strong style="font-size:15px">Solicitud ${esc(g.solicitud)}</strong>
        <span style="color:${C.suave}">${g.autorizadoPor ? 'Autorizo ' + esc(g.autorizadoPor) : 'Sin dato de quien autorizo'}</span>
      </div>
      <ul style="list-style:none;margin:8px 0 0;padding:0">${filas}</ul>
    </div>`;
  }).join('');
  return `<div data-dc-por-solicitud style="margin-top:20px;font-size:14px;color:${C.texto}">
      <h3 style="margin:0 0 4px;font-size:15px">Ajustes aprobados por solicitud</h3>
      <p style="margin:0;color:${C.suave}">Lo que se corrigio porque se aprobo una solicitud. Incluye los danos que quedaron en cero y por eso no salen arriba.</p>
      ${tarjetas}
    </div>`;
}

const C = {
  panel: 'var(--bg-secondary)',
  borde: 'var(--border-default)',
  texto: 'var(--text-strong)',
  suave: 'var(--text-secondary)',
  ok: 'var(--accent-green-text)',
  alerta: 'var(--accent-yellow)',
  malo: 'var(--accent-red)'
};

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const nfmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('es-CO'));
const pct = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`);

/** Rango del mes que se esta viendo. Por defecto, el mes en curso. */
function rangoMes(offset = 0) {
  const hoy = new Date();
  const d = new Date(hoy.getFullYear(), hoy.getMonth() + offset, 1);
  const fin = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return {
    desde: iso(d),
    hasta: iso(fin),
    etiqueta: formatearFecha(d, { month: 'long', year: 'numeric' })
  };
}

/**
 * El bloque que decide si el resto se puede creer.
 * DATO + CONTEXTO + ACCION, como manda la doctrina de indicadores comestibles.
 */
function bloqueFiabilidad(d) {
  const f = d.fiabilidadPct;
  const total = d.atribuidoPorConteo + d.atribuidoPorUsuario + d.sinIdentificar;

  // MES VACIO: sin movimientos NO se pinta porcentaje ni semaforo (observacion de Q,
  // bitacora #9693; el hueco salio en el #9640).
  //
  // Antes, con cero movimientos, fiabilidadPct quedaba en 0 y como 0 < 60 se pintaba
  // ROJO junto al aviso de no evaluar a nadie. La pantalla se contradecia: decia
  // -no hubo movimientos anomalos-, que es buena noticia, y al lado gritaba 0,0% con
  // cara de alarma. Pasa el primero de cada mes y en cualquier negocio tranquilo.
  //
  // El 0% se reserva para cuando SI hubo movimientos y ninguno se pudo atribuir: ahi
  // si es una alarma de verdad y debe verse roja.
  if (total === 0) {
    return `
    <div style="background:${C.panel};border:1px solid ${C.borde};border-left:4px solid ${C.suave};border-radius:10px;padding:16px 18px;margin-bottom:16px">
      <div style="font-size:1rem;color:${C.texto};font-weight:600">Sin datos aun</div>
      <div style="font-size:0.875rem;color:${C.suave};margin-top:6px;line-height:1.6">
        Este mes todavia no hay danos ni entradas de ajuste registrados. No es que falte
        informacion: es que no ha pasado nada anomalo.
      </div>
    </div>`;
  }

  const color = f >= 90 ? C.ok : f >= 60 ? C.alerta : C.malo;

  // Aqui total ya es > 0: el caso vacio salio arriba con su propio bloque.
  // D-39 (nexo-api 302): con la regla unica ya no hay capa de sesion ni tablas abajo; el texto
  // lo decide la forma de la respuesta (textosDeDanos), asi no miente con ninguna de las dos APIs.
  const { contexto: textoContexto, accion } = textoDeFiabilidad(d);
  const contexto = esc(textoContexto);
  const advertencia = advertenciaAMostrar(d);

  return `
    <div style="background:${C.panel};border:1px solid ${C.borde};border-left:4px solid ${color};border-radius:10px;padding:16px 18px;margin-bottom:16px">
      <div style="display:flex;align-items:baseline;gap:12px;flex-wrap:wrap">
        <span style="font-size:1.9rem;font-weight:700;color:${color};line-height:1">${pct(f)}</span>
        <span style="font-size:0.875rem;color:${C.texto};font-weight:600">se puede atribuir a quien de verdad lo hizo</span>
      </div>
      <div style="font-size:0.875rem;color:${C.suave};margin-top:8px;line-height:1.6">${contexto}</div>
      <div style="font-size:0.875rem;color:${color};margin-top:8px;line-height:1.6;font-weight:600">${esc(accion)}</div>
      ${advertencia ? `<div style="font-size:0.875rem;color:${C.suave};margin-top:8px;font-style:italic">${esc(advertencia)}</div>` : ''}
    </div>`;
}

/** Una tabla por capa. El titulo y la nota dicen que se puede concluir de ella. */
function tablaCapa(titulo, nota, filas, color) {
  if (!filas.length) return '';
  const cuerpo = filas.map((f) => `
    <tr>
      <td style="padding:9px 10px;border-bottom:1px solid var(--bg-tertiary);color:${C.texto};font-size:0.875rem">${esc(f.colaborador)}</td>
      <td style="padding:9px 10px;border-bottom:1px solid var(--bg-tertiary);text-align:right;font-family:monospace;font-size:14px;color:${C.texto}">${nfmt(f.danosMovimientos)}</td>
      <td style="padding:9px 10px;border-bottom:1px solid var(--bg-tertiary);text-align:right;font-family:monospace;font-size:14px;color:${C.suave}">${nfmt(f.danosUnidades)}</td>
      <td style="padding:9px 10px;border-bottom:1px solid var(--bg-tertiary);text-align:right;font-family:monospace;font-size:14px;color:${C.texto}">${nfmt(f.entradasAjusteMovimientos)}</td>
      <td style="padding:9px 10px;border-bottom:1px solid var(--bg-tertiary);text-align:right;font-family:monospace;font-size:14px;color:${C.suave}">${nfmt(f.entradasAjusteUnidades)}</td>
    </tr>`).join('');

  return `
    <div style="margin-bottom:18px">
      <div style="font-size:0.875rem;color:${color};font-weight:700;margin-bottom:4px">${esc(titulo)}</div>
      <div style="font-size:0.875rem;color:${C.suave};margin-bottom:8px;line-height:1.5">${nota}</div>
      <div style="overflow-x:auto">
        <table style="width:100%;border-collapse:collapse;min-width:520px">
          <thead>
            <tr style="font-size:0.875rem;color:${C.suave};text-transform:uppercase;letter-spacing:.05em">
              <th style="text-align:left;padding:8px 10px;border-bottom:1px solid ${C.borde}">Colaborador</th>
              <th style="text-align:right;padding:8px 10px;border-bottom:1px solid ${C.borde}">Danos</th>
              <th style="text-align:right;padding:8px 10px;border-bottom:1px solid ${C.borde}">Unidades</th>
              <th style="text-align:right;padding:8px 10px;border-bottom:1px solid ${C.borde}">Entradas ajuste</th>
              <th style="text-align:right;padding:8px 10px;border-bottom:1px solid ${C.borde}">Unidades</th>
            </tr>
          </thead>
          <tbody>${cuerpo}</tbody>
        </table>
      </div>
    </div>`;
}

/** Fail-open: decir la causa, no mostrar ceros. */
function bloqueSinDatos(motivo, detalle) {
  const frases = {
    NO_PUBLICADO: 'Todavia no podemos mostrar este resumen porque la consulta no esta publicada.',
    SIN_SESION: 'Tu sesion no tiene permiso para ver estos movimientos.',
    PROHIBIDO: 'Este resumen es solo para el dueno o el administrativo del negocio.',
    SIN_RED: 'No pudimos hablar con el servidor en este momento.',
    SIN_NEGOCIO: 'Elige un negocio arriba para ver sus movimientos.',
    ERROR_API: 'El servidor respondio con un error.'
  };
  return `
    <div style="background:${C.panel};border:1px solid ${C.alerta};border-left:3px solid ${C.alerta};border-radius:8px;padding:16px">
      <div style="color:${C.alerta};font-weight:700;font-size:0.875rem;margin-bottom:6px">${esc(frases[motivo] || frases.ERROR_API)}</div>
      <div style="color:${C.suave};font-size:0.875rem;line-height:1.5">${esc(detalle || '')}</div>
      <div style="color:${C.suave};font-size:0.875rem;margin-top:8px;font-style:italic">
        No se muestran ceros a proposito: un cero sin explicacion se lee como "no paso nada",
        y eso seria mentir.
      </div>
    </div>`;
}

/**
 * Punto de entrada de la vista.
 * @param {HTMLElement} container
 */
export async function renderDanosColaborador(container) {
  setPageTitle?.('Danos por colaborador');
  let offset = 0;

  async function pintar() {
    const biz = getSelectedBusiness();
    const bizId = biz?.id || biz?.businessId || biz;
    const r = rangoMes(offset);

    container.innerHTML = `
      <div class="detail-section">
        ${regresoHtml()}
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:6px">
          <div>
            <h2 style="margin:0;font-size:1rem">Danos y ajustes por colaborador</h2>
            <p style="font-size:0.875rem;color:${C.suave};margin-top:2px">
              Mes operativo: cada ajuste cuenta en el dia operativo del conteo (D-39).
              Solo cuenta lo anomalo: danos y entradas de ajuste. El desperdicio y el aumento
              son parte normal de la operacion y no se le cargan a nadie.
            </p>
          </div>
          <div style="display:flex;gap:6px;align-items:center">
            <button id="nx-dc-prev" class="btn btn-ghost btn-sm" style="font-size:0.875rem">&larr; Mes anterior</button>
            <span id="nx-dc-mes" style="font-size:0.875rem;color:${C.texto};font-weight:600;min-width:130px;text-align:center">${esc(r.etiqueta)}</span>
            <button id="nx-dc-next" class="btn btn-ghost btn-sm" style="font-size:0.875rem"${offset >= 0 ? ' disabled' : ''}>Mes siguiente &rarr;</button>
          </div>
        </div>
        <div id="nx-dc-body" style="margin-top:14px">
          <div style="color:${C.suave};font-size:0.875rem;padding:12px">Cargando...</div>
        </div>
        <div id="nx-dc-vigentes" data-dc-vigentes style="margin-top:20px"></div>
      </div>`;
    // INV-CRUCES-PRESENTACION-T1 (D-138): la lista de danos uno por uno, con su compensacion. Independiente de la tabla
    // por colaborador: si una de las dos rutas falta, la otra se pinta igual.
    pintarVigentes(bizId, r);

    document.getElementById('nx-dc-prev')?.addEventListener('click', () => { offset -= 1; pintar(); });
    document.getElementById('nx-dc-next')?.addEventListener('click', () => { if (offset < 0) { offset += 1; pintar(); } });

    const res = await MovimientosColaboradorApiClient.getPorColaborador(bizId, { desde: r.desde, hasta: r.hasta });
    const $body = document.getElementById('nx-dc-body');
    if (!$body) return;

    if (!res.ok) {
      $body.innerHTML = bloqueSinDatos(res.motivo, res.detalle);
      return;
    }

    const d = res.data;
    const de = (capa) => d.items.filter((f) => (f.capa || '').toLowerCase() === capa);
    // Lo que no cae en ninguna capa conocida no se esconde: se muestra como no atribuible.
    const conocidas = ['conteo', 'usuario', 'propietario'];
    const otras = d.items.filter((f) => !conocidas.includes((f.capa || '').toLowerCase()));

    $body.innerHTML = bloqueFiabilidad(d)
      + tablaCapa(
          'Se sabe quien conto',
          'Estos ajustes nacieron de una sesion de conteo, asi que el nombre es el de quien '
            + 'conto. <strong>Es la unica tabla con la que se puede comparar a una persona con otra.</strong>',
          de('conteo'), C.ok)
      + tablaCapa(
          'Solo se sabe quien tenia la sesion abierta',
          'No hubo sesion de conteo ese dia: son correcciones manuales. El nombre es el de quien '
            + 'estaba conectado al programa, que no siempre es quien lo hizo. No compares esta tabla '
            + 'con la de arriba.',
          de('usuario'), C.alerta)
      + tablaCapa(
          'Del propietario',
          'Movimientos hechos con el usuario del dueno.',
          de('propietario'), C.suave)
      + tablaCapa(
          'Sin identificar',
          'Movimientos viejos que no registraron quien fue. No es que nadie los haya hecho: es que '
            + 'el dato no se guardaba. Quedan fuera de cualquier comparacion.',
          otras, C.suave);
  }

  await pintar();
}
